import { useCallback } from "react";
import { Plus, Trash2, FolderPlus } from "lucide-react";
import {
  OPERATORS_BY_TYPE,
  OPERATOR_LABEL,
  SEGMENT_FIELDS,
  VALUELESS_OPERATORS,
  makeCondition,
  makeGroup,
  type FieldDef,
  type FilterCondition,
  type FilterGroup,
  type FilterNode,
  type FilterOperator,
  type FilterTree,
  type ViewEntityType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Phase 7 sub-bucket 17 — SPA port of components/shared/segments/FilterTreeBuilder.tsx
// (662 LOC legacy). The builder is fully controlled: all mutations go
// through `onChange` with a freshly-built copy of the tree — we never
// mutate `value` in place.
//
// Differences from the legacy file:
//   - Reads field metadata from @colab/shared/segments-meta (hoisted in
//     this sub-bucket) instead of /lib/segments.
//   - No "use client" directive — react-router SPA, every component is
//     client-side already.
//   - No router-side state; the consumer (typically a Popover-wrapped
//     <AddFilterMenu>) buffers the draft and forwards committed FilterTree
//     instances back to the URL via setSearchParams.

type FilterTreeBuilderProps = {
  entityType: ViewEntityType;
  value: FilterTree;
  onChange: (next: FilterTree) => void;
  disabled?: boolean;
};

export function FilterTreeBuilder({
  entityType,
  value,
  onChange,
  disabled = false,
}: FilterTreeBuilderProps) {
  const fields = SEGMENT_FIELDS[entityType] ?? [];

  const updateAt = useCallback(
    (path: number[], updater: (node: FilterNode) => FilterNode) => {
      const next = mutateAt(value, path, updater);
      if (next.kind !== "group") return;
      onChange(next);
    },
    [value, onChange],
  );

  const removeAt = useCallback(
    (path: number[]) => {
      if (path.length === 0) return;
      const parentPath = path.slice(0, -1);
      const idx = path[path.length - 1];
      const next = mutateAt(value, parentPath, (parent) => {
        if (parent.kind !== "group") return parent;
        return {
          ...parent,
          children: parent.children.filter((_, i) => i !== idx),
        };
      });
      if (next.kind !== "group") return;
      onChange(next);
    },
    [value, onChange],
  );

  return (
    <div className="space-y-2">
      <GroupNode
        node={value}
        path={[]}
        depth={0}
        disabled={disabled}
        fields={fields}
        onUpdate={updateAt}
        onRemove={removeAt}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

type SharedProps = {
  path: number[];
  depth: number;
  disabled: boolean;
  fields: readonly FieldDef[];
  onUpdate: (path: number[], updater: (node: FilterNode) => FilterNode) => void;
  onRemove: (path: number[]) => void;
};

function GroupNode({
  node,
  ...rest
}: { node: FilterGroup } & SharedProps) {
  const { path, depth, disabled, fields, onUpdate, onRemove } = rest;
  const isRoot = path.length === 0;
  const childCount = node.children.length;

  function toggleCombinator() {
    onUpdate(path, (n) => {
      if (n.kind !== "group") return n;
      return { ...n, combinator: n.combinator === "AND" ? "OR" : "AND" };
    });
  }

  function addCondition() {
    onUpdate(path, (n) => {
      if (n.kind !== "group") return n;
      const firstField = fields[0];
      if (!firstField) return n;
      return { ...n, children: [...n.children, makeCondition(firstField)] };
    });
  }

  function addGroup() {
    onUpdate(path, (n) => {
      if (n.kind !== "group") return n;
      const nested = makeGroup(n.combinator === "AND" ? "OR" : "AND");
      return { ...n, children: [...n.children, nested] };
    });
  }

  return (
    <div
      className={cn(
        "rounded-lg border bg-card/40",
        depth > 0 && "border-dashed",
        depth === 0 && "p-3",
        depth > 0 && "p-3",
      )}
      style={depth > 0 ? { marginLeft: 16 } : undefined}
    >
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={toggleCombinator}
          disabled={disabled || childCount < 2}
          aria-label={`Toggle combinator — currently ${node.combinator}`}
          className={cn(
            "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[10px] font-semibold uppercase tracking-wider transition-colors",
            node.combinator === "AND"
              ? "border-accent/40 bg-accent/10 text-accent"
              : "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
            (disabled || childCount < 2) &&
              "cursor-not-allowed opacity-60 hover:bg-transparent",
          )}
        >
          {node.combinator}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {childCount === 0
            ? "No conditions yet"
            : childCount === 1
              ? "1 condition"
              : `${childCount} children · matches ${
                  node.combinator === "AND" ? "ALL" : "ANY"
                }`}
        </span>

        {!isRoot && (
          <button
            type="button"
            onClick={() => onRemove(path)}
            disabled={disabled}
            className="ml-auto inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Remove group"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>

      {childCount > 0 ? (
        <ul className="space-y-2">
          {node.children.map((child, idx) => (
            <li key={idx}>
              {child.kind === "condition" ? (
                <ConditionRow
                  node={child}
                  path={[...path, idx]}
                  depth={depth + 1}
                  disabled={disabled}
                  fields={fields}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                />
              ) : (
                <GroupNode
                  node={child}
                  path={[...path, idx]}
                  depth={depth + 1}
                  disabled={disabled}
                  fields={fields}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs italic text-muted-foreground">
          {isRoot ? "Add a condition to start filtering." : "Empty group."}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCondition}
          disabled={disabled || fields.length === 0}
          className={cn(
            "h-7 text-xs",
            isRoot &&
              childCount === 0 &&
              "border-accent/40 text-accent hover:bg-accent/5",
          )}
        >
          <Plus className="mr-1 h-3 w-3" />
          Add condition
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addGroup}
          disabled={disabled || fields.length === 0}
          className="h-7 text-xs text-muted-foreground"
        >
          <FolderPlus className="mr-1 h-3 w-3" />
          Add group
        </Button>
      </div>
    </div>
  );
}

function ConditionRow({
  node,
  ...rest
}: { node: FilterCondition } & SharedProps) {
  const { path, disabled, fields, onUpdate, onRemove } = rest;
  const field = fields.find((f) => f.key === node.field);
  const operators = field
    ? OPERATORS_BY_TYPE[field.type]
    : (["equals"] as readonly FilterOperator[]);

  function setField(nextKey: string) {
    const nextField = fields.find((f) => f.key === nextKey);
    if (!nextField) return;
    onUpdate(path, (n) => {
      if (n.kind !== "condition") return n;
      const validOps = OPERATORS_BY_TYPE[nextField.type];
      const nextOp = validOps.includes(n.operator) ? n.operator : validOps[0];
      const nextValue = nextField.type === "boolean" ? true : undefined;
      return {
        ...n,
        field: nextKey,
        operator: nextOp,
        value: nextValue,
      };
    });
  }

  function setOperator(nextOp: string) {
    onUpdate(path, (n) => {
      if (n.kind !== "condition") return n;
      const op = nextOp as FilterOperator;
      const value = VALUELESS_OPERATORS.has(op) ? undefined : n.value;
      return { ...n, operator: op, value };
    });
  }

  function setValue(nextValue: FilterCondition["value"]) {
    onUpdate(path, (n) => {
      if (n.kind !== "condition") return n;
      return { ...n, value: nextValue };
    });
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border bg-background/60 px-2.5 py-2"
      style={{ marginLeft: 16 }}
    >
      <Select
        value={node.field}
        onValueChange={setField}
        disabled={disabled || !field}
      >
        <SelectTrigger className="h-8 w-[180px] text-xs">
          <SelectValue placeholder="Field" />
        </SelectTrigger>
        <SelectContent>
          {fields.map((f) => (
            <SelectItem key={f.key} value={f.key} className="text-xs">
              {f.label}
            </SelectItem>
          ))}
          {!field && (
            <SelectItem value={node.field} className="text-xs italic">
              {node.field} (removed)
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      <Select
        value={node.operator}
        onValueChange={setOperator}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-[180px] text-xs">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op} className="text-xs">
              {OPERATOR_LABEL[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!VALUELESS_OPERATORS.has(node.operator) && field && (
        <ValueInput
          field={field}
          operator={node.operator}
          value={node.value}
          onChange={setValue}
          disabled={disabled}
        />
      )}

      <button
        type="button"
        onClick={() => onRemove(path)}
        disabled={disabled}
        className="ml-auto inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Remove condition"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value editor — switches on field type + operator.
// ---------------------------------------------------------------------------

type ValueInputProps = {
  field: FieldDef;
  operator: FilterOperator;
  value: FilterCondition["value"];
  onChange: (next: FilterCondition["value"]) => void;
  disabled?: boolean;
};

function ValueInput({
  field,
  operator,
  value,
  onChange,
  disabled,
}: ValueInputProps) {
  if (operator === "between") {
    const tuple =
      Array.isArray(value) && value.length === 2 ? value : ["", ""];
    const [from, to] = tuple as [string | number, string | number];
    const inputType = field.type === "date" ? "date" : "number";

    const fromIsFilled =
      typeof from === "number" || (typeof from === "string" && from.length > 0);
    const toIsFilled =
      typeof to === "number" || (typeof to === "string" && to.length > 0);
    const partiallyFilled =
      (fromIsFilled && !toIsFilled) || (!fromIsFilled && toIsFilled);

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <Input
            type={inputType}
            value={String(from ?? "")}
            onChange={(e) =>
              onChange(coerceTuple(field.type, [e.target.value, to ?? ""]))
            }
            disabled={disabled}
            className="h-8 w-[140px] text-xs"
            placeholder="from"
          />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            and
          </span>
          <Input
            type={inputType}
            value={String(to ?? "")}
            onChange={(e) =>
              onChange(coerceTuple(field.type, [from ?? "", e.target.value]))
            }
            disabled={disabled}
            className="h-8 w-[140px] text-xs"
            placeholder="to"
          />
        </div>
        {partiallyFilled && (
          <p className="text-xs text-destructive">Both values required</p>
        )}
      </div>
    );
  }

  if (operator === "in" || operator === "not_in") {
    if (field.type === "enum" && field.enumValues) {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <Input
          type="text"
          value={arr.join(", ")}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s.length > 0),
            )
          }
          disabled={disabled}
          className="h-8 w-[240px] text-xs"
          placeholder={`e.g. ${field.enumValues.slice(0, 2).join(", ")}`}
        />
      );
    }
    const arr = Array.isArray(value) ? (value as Array<string | number>) : [];
    return (
      <Input
        type="text"
        value={arr.join(", ")}
        onChange={(e) => {
          const parts = e.target.value
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          if (field.type === "number") {
            onChange(parts.map(Number).filter((n) => Number.isFinite(n)));
          } else {
            onChange(parts);
          }
        }}
        disabled={disabled}
        className="h-8 w-[240px] text-xs"
        placeholder="value, value, ..."
      />
    );
  }

  if (operator === "in_last_days" || operator === "in_next_days") {
    return (
      <Input
        type="number"
        min={1}
        step={1}
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(undefined);
            return;
          }
          const n = Number(raw);
          if (!Number.isFinite(n)) {
            onChange(undefined);
            return;
          }
          onChange(Math.floor(n));
        }}
        onBlur={(e) => {
          // On blur, clamp to a positive integer. HTML `min={1}` is advisory
          // only; users can submit `-5` or `0.5` without this guard.
          const raw = e.target.value;
          if (raw === "") return;
          const parsed = parseFloat(raw);
          const clamped = Math.max(
            1,
            Math.floor(Number.isFinite(parsed) ? parsed : 1),
          );
          if (clamped !== value) onChange(clamped);
        }}
        disabled={disabled}
        className="h-8 w-[100px] text-xs"
        placeholder="days"
      />
    );
  }

  // Single-value path: type drives the input control.
  if (field.type === "enum" && field.enumValues) {
    return (
      <Select
        value={typeof value === "string" ? value : ""}
        onValueChange={(v) => onChange(v)}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-[180px] text-xs">
          <SelectValue placeholder="Pick a value" />
        </SelectTrigger>
        <SelectContent>
          {field.enumValues.map((v) => (
            <SelectItem key={v} value={v} className="text-xs">
              {field.enumLabels?.[v] ?? v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (field.type === "boolean") {
    return (
      <Select
        value={value === true ? "true" : value === false ? "false" : ""}
        onValueChange={(v) => onChange(v === "true")}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-[120px] text-xs">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true" className="text-xs">
            true
          </SelectItem>
          <SelectItem value="false" className="text-xs">
            false
          </SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (field.type === "number") {
    return (
      <Input
        type="number"
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(
            Number.isFinite(n) && e.target.value !== "" ? n : undefined,
          );
        }}
        disabled={disabled}
        className="h-8 w-[160px] text-xs"
        placeholder="0"
      />
    );
  }

  if (field.type === "date") {
    return (
      <Input
        type="date"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled}
        className="h-8 w-[160px] text-xs"
      />
    );
  }

  // text
  return (
    <Input
      type="text"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-8 w-[200px] text-xs"
      placeholder="value"
    />
  );
}

type LocalFieldType = FieldDef["type"];

function coerceTuple(
  fieldType: LocalFieldType,
  pair: [string | number, string | number],
): [string, string] | [number, number] {
  if (fieldType === "number") {
    // Promote to `[number, number]` only when BOTH cells parse as finite
    // numbers — otherwise keep the tuple as `[string, string]` so the
    // Zod string-tuple variant in `filterValueSchema` accepts the half-
    // typed state without `Number("") || 0` silently saving a 0.
    const aRaw = String(pair[0] ?? "");
    const bRaw = String(pair[1] ?? "");
    if (aRaw.length === 0 || bRaw.length === 0) return [aRaw, bRaw];
    const aNum = Number(aRaw);
    const bNum = Number(bRaw);
    if (!Number.isFinite(aNum) || !Number.isFinite(bNum)) {
      return [aRaw, bRaw];
    }
    return [aNum, bNum];
  }
  return [String(pair[0] ?? ""), String(pair[1] ?? "")];
}

function mutateAt(
  root: FilterNode,
  path: number[],
  updater: (node: FilterNode) => FilterNode,
): FilterNode {
  if (path.length === 0) return updater(root);
  if (root.kind !== "group") return root;
  const [head, ...rest] = path;
  return {
    ...root,
    children: root.children.map((child, idx) =>
      idx === head ? (mutateAt(child, rest, updater) as FilterNode) : child,
    ),
  };
}
